import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Modal,
    Row,
    Col,
    Form,
} from "react-bootstrap";
import validate from "validate.js";
import {
  hideWeeklyTimesheetValidationError,
  createOrUpdateWeeklyTimesheet,
} from "actions";
import { ProgramDropdown, TimesheetTaskDropdown } from "CommonDropdowns";
import moment from "moment";
import { isEmpty } from "lodash";
import { getWeekEndingDates } from "utils/general";

const schema = {
  program_id: {
    presence: { allowEmpty: false, message: '^Program is required' },
  },
  task_ids: {
    presence: { allowEmpty: false, message: '^Task is required' },
  },
  week_end_date: {
    presence: { allowEmpty: false, message: '^Week is required' },
  }
};

const AddTimeModal = ({ show, handleClose,  setCurrentWeek, extraFiltersState, setExtraFiltersState }) => {
    
    const dispatch = useDispatch();

    const session = useSelector((s) => s.session);
    const weeklyTimesheetState = useSelector((s) => s.weeklyTimesheetState);
    const object_viewed_id = session?.current_page_permissions?.object_id || "";

    const [weekOptions, setWeekOptions] = useState([]);
    const [formState, setFormState] = useState({
      isValid: false,
      values: {
        program_id: '',
        ['week_end_date']: extraFiltersState.values.week_end_date,
      },
      touched: {
        ['week_end_date']: true,
      },
      errors: {}
    });
    const [programValue, setProgramValue] = useState(null);
    const [taskValue, setTaskValue] = useState(null);

    useEffect(() => {
      setWeekOptions(getWeekEndingDates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { 
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            ['week_end_date']: extraFiltersState.values.week_end_date,
        },
        touched: {
            ...formState.touched,
            ['week_end_date']: true,
        },
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraFiltersState.values.week_end_date]);

    useEffect(() => {
      if (weeklyTimesheetState.validation_error) {
        setFormState((formState) => ({
            ...formState,
            isValid: false,
            errors: weeklyTimesheetState.validation_error || {},
        }));
      }
    }, [weeklyTimesheetState.validation_error]);

    useEffect(() => {
      const errors = validate(formState.values, schema);
      setFormState(formState => ({
          ...formState,
          isValid: !errors,
          errors: errors || {}
      }));
    }, [formState.values]);

    const handleChange = (event) => {
      event.persist();
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            [event.target.name]: event.target.value,
        },
        touched: {
            ...formState.touched,
            [event.target.name]: true,
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError(event.target.name));
    };

    const handleProgramChange = (selected) => { 
      setProgramValue(selected);
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          program_id: !isEmpty(selected) ? selected.value : '',
          task_ids: !isEmpty(selected) ? formState.values.task_ids : [], // Reset task_ids if program is cleared
        },
        touched: {
          ...formState.touched,
          program_id: true,
        },
      }));
      // Reset task value when program changes
      if (isEmpty(selected)) {
        setTaskValue(null);
      }
      dispatch(hideWeeklyTimesheetValidationError('program_id'));
    }

    const handleTaskChange = (selected) => {
      setTaskValue(selected);
      setFormState((formState) => ({
        ...formState,
        values: {
            ...formState.values,
            task_ids: selected ? selected.map(item => item.value) : [],
        },
        touched: {
            ...formState.touched,
            task_ids: true,
        },
      }));
      dispatch(hideWeeklyTimesheetValidationError('task_ids'));
    }

    const onFormSubmit = (event) => {
      event.preventDefault();

      if(formState.isValid){
        dispatch(createOrUpdateWeeklyTimesheet(formState.values, 'POST', object_viewed_id))
        .then(() => {
          //onModelClose();
          handleClose();
          /*setExtraFiltersState((extraFiltersState) => ({
            ...extraFiltersState,
            values: {
                ...extraFiltersState.values,
                week_end_date: formState.values.week_end_date,
            }
          }));*/
          setCurrentWeek(formState.values.week_end_date);

          setFormState({
            isValid: false,
            values: {
              week_end_date: formState.values.week_end_date,
            },
            touched: {
              week_end_date: true,
            },
            errors: {}
          });

          setProgramValue(null);
          setTaskValue(null);
        })
        .catch(() => {
          // Error is already handled by the action
        });
      }
    };

    const hasError = (field) =>
      formState.touched[field] && formState.errors[field] ? true : false;

    const onModelClose = () => {
      handleClose();

      /*setExtraFiltersState((extraFiltersState) => ({
        ...extraFiltersState,
        values: {
            ...extraFiltersState.values,
            week_end_date: formState.values.week_end_date,
        }
      }));*/
      //setCurrentWeek(formState.values.week_end_date);


      setFormState({
        isValid: false,
        values: {
          week_end_date: extraFiltersState.values.week_end_date,
        },
        touched: {
          week_end_date: true,
        },
        errors: {}
      });

      setProgramValue(null);
      setTaskValue(null);
    }

    return (
        <Modal
            show={show}
            onHide={onModelClose}
            centered
            className="add-new-program"
        >
            <div className="p-2">
                <Modal.Header className="p-2" closeButton>
                    <Modal.Title>
                        <h5 className="font-20 font-weight-bold heading-color">
                            Add Time
                        </h5>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form onSubmit={onFormSubmit}>
                        
                    <Form.Group className="mb-3">
                            <Form.Label className="label-input">Week Ending</Form.Label>
                            <Form.Select 
                                className={`input-field ${hasError('week_end_date') ? 'is-invalid' : ''}`}
                                name="week_end_date"
                                onChange={handleChange}
                                value={formState.values.week_end_date || ''}
                            >
                              <option value="">Select Week</option>
                              {weekOptions.map(week_end_date => (
                                <option key={week_end_date} value={week_end_date}>Week Ending {moment(week_end_date, 'YYYY-MM-DD').format('MM/DD/YYYY')}</option>
                              ))}
                            </Form.Select>
                            {hasError('week_end_date') && (
                                <Form.Control.Feedback type="invalid">
                                    {formState.errors.week_end_date[0]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        
                        <ProgramDropdown
                          programValue={programValue}
                          programOnChange={handleProgramChange}
                          setProgramValue={setProgramValue}
                          isMulti={false}
                          trackingProgram={1}
                          loadUserPrograms={true}
                          user_id={session?.user?.id || null} 
                          error={hasError('program_id') ? formState.errors.program_id[0] : ''}
                        />

                        {formState.values.program_id && (
                          <>
                            <TimesheetTaskDropdown
                                taskValue={taskValue}
                                taskOnChange={handleTaskChange}
                                setTaskValue={setTaskValue}
                                isMulti={true}
                                programId={formState.values.program_id}
                                showSelectAllOption={false}
                                loadUnbinded={false}
                                error={hasError('task_ids') ? formState.errors.task_ids[0] : ''}
                            />
                            {hasError("task_ids") && (
                              <div className="invalid-feedback d-block">
                                {formState.errors.task_ids[0]}
                              </div>
                            )}
                          </>
                        )}

                       

                        {/* Action Buttons */}
                        <Row className="mt-5">
                            <Col lg={6}>
                                <button
                                    type="button"
                                    className="c-b-btn w-100"
                                    onClick={onModelClose}
                                >
                                    Cancel
                                </button>
                            </Col>
                            <Col lg={6}>
                                <button
                                    type="submit"
                                    className="n-u-btn w-100"
                                    disabled={!formState.isValid}
                                >
                                    Add
                                </button>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default AddTimeModal;
